import { useUserData } from '@contexts/UserDataContext'
import { useReadingSession } from '@hooks/useReadingSession'
import { useVerseNavigation } from '@hooks/useVerseNavigation'
import { useRouter } from 'next/router'
import { useCallback } from 'react'
import { TOTAL_QURAN_VERSES } from '@components/shared/consts'
import ReadingBottomNav from './ReadingBottomNav'
import ReadingProgressBar from './ReadingProgressBar'
import TopStatsBar from './TopStatsBar'
import VerseCard from './VerseCard'
import LoadingSpinner from '@components/shared/Loading/Loading'
import SwipeIndicator from '@components/shared/PaginationAndSettingsRow/SwipeIndicator'

interface ReadingScreenProps {
  onEditGoal?: () => void
  mode?: 'daily' | 'standalone'
  initialVerse?: string | number
}

const ReadingScreen = ({ onEditGoal, mode = 'daily', initialVerse }: ReadingScreenProps) => {
  const router = useRouter()
  const { userData } = useUserData()
  const challenge = userData?.readingChallenge
  const { versesReadToday, totalVersesRead, goalCompletedToday } = useReadingSession()

  const {
    verseData,
    isLoading,
    activeVerseNum,
    handleNext,
    handlePrevious,
    swipeState,
    scrollContainerRef,
  } = useVerseNavigation({
    mode,
    initialVerse,
    challengeCurrentVerse: challenge?.currentVerse,
  })

  const handleDone = useCallback(() => {
    router.push('/')
  }, [router])

  if (mode === 'daily' && !challenge) return <LoadingSpinner />

  return (
    <div className="min-h-screen bg-main relative overflow-hidden flex flex-col texture-noise">
      {/* Background glow */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-brand-secondary/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-primary-500/10 blur-[120px] rounded-full pointer-events-none" />

      {/* Top stats bar */}
      <TopStatsBar versesRead={totalVersesRead} onEditGoal={onEditGoal} mode={mode} />

      {/* Progress bar */}
      {mode === 'daily' && challenge && (
        <ReadingProgressBar
          versesReadToday={versesReadToday}
          dailyGoal={challenge.dailyGoal}
          currentVerse={activeVerseNum}
        />
      )}

      {/* Verse card */}
      <div ref={scrollContainerRef} className="flex-1 px-0 sm:px-4 py-3 overflow-y-auto pb-24">
        <VerseCard verse={verseData} isLoading={isLoading} />
      </div>

      {/* Bottom navigation */}
      <ReadingBottomNav
        onPrevious={handlePrevious}
        onNext={handleNext}
        onDone={handleDone}
        isFirstVerse={activeVerseNum <= 1}
        isLastVerse={activeVerseNum >= TOTAL_QURAN_VERSES}
        goalCompleted={mode === 'daily' ? goalCompletedToday : false}
      />

      <SwipeIndicator swipeState={swipeState} />
    </div>
  )
}

export default ReadingScreen
